import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface AIConfig {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
}

// Function to generate chat completions using Gemini API via Supabase edge function
export async function generateGeminiCompletion(
  messages: Array<{ role: string; parts: Array<{ text?: string, inline_data?: { mime_type: string, data: string } }> }>,
  model: string = "gemini-1.5-pro-latest",
  chatId?: string,
  config?: AIConfig
): Promise<{ content: string; tokensUsed: number }> {
  // Add enhanced retry logic for network failures and service outages
  let retries = 5; // Increase max retries
  let lastError: any = null;
  let backoffDelay = 1000; // Start with 1 second delay, will increase exponentially
  
  const apiKey = localStorage.getItem('gemini_api_key');

  while (retries > 0) {
    try {
      console.log(`Sending request to Gemini with model: ${model}`);
      
      // Log the request for debugging
      console.log('Sending full message structure to Gemini:', JSON.stringify(messages, null, 2));

      const { data, error } = await supabase.functions.invoke('gemini-proxy', {
        body: {
          model: model,
          messages: messages,
          apiKey: apiKey, // Pass the API key if available
          chat_id: chatId,
          generationConfig: {
            temperature: config?.temperature,
            maxOutputTokens: config?.max_tokens,
            topP: config?.top_p,
          }
        }
      });

      if (error) {
        console.error('Error invoking Gemini proxy function:', error);
        throw new Error(error.message || 'Failed to get response from Gemini');
      }

      if (!data || data.error) {
        const errorMessage = data?.error?.message || 'Failed to get response from Gemini';
        console.error('Gemini API response error:', data?.error);
        
        // Handle specific error cases based on Gemini API responses
        if (errorMessage.includes('API key')) {
          throw new Error("Invalid API key configuration for Gemini. Please contact the administrator or check your settings.");
        } else if (errorMessage.includes('quota')) {
          throw new Error("Gemini billing or quota issue. Please contact the administrator.");
        } else if (errorMessage.includes('model')) {
          throw new Error(`Gemini model error: ${errorMessage}. Please try a different model.`);
        } else if (errorMessage.includes('safety')) {
           throw new Error("Your prompt was rejected by Gemini's safety system. Please modify your prompt.");
        }
        
        throw new Error(errorMessage);
      }
      
      console.log("Response received from Gemini");
      
      // Extract content from the response (adjust based on actual Gemini response structure)
      // Assuming the response structure is similar to OpenAI chat completions
      if (data.candidates && data.candidates.length > 0 && data.candidates[0].content?.parts?.length > 0) {
         const content = data.candidates[0].content.parts.map(part => part.text).join('');
         const tokensUsed = data.usageMetadata?.totalTokenCount || 0;
         return { content, tokensUsed };
      } else {
         throw new Error("Unexpected response format from Gemini");
      }

    } catch (error: any) {
      lastError = error;
      
      // Only retry certain types of errors
      const isRetryableError = 
        error.message.includes('temporarily unavailable') || 
        error.message.includes('Network') || 
        error.message.includes('service') ||
        error.message.includes('Too many requests') ||
        error.message.includes('timeout') ||
        error.message.includes('429') || // Too many requests
        error.message.includes('503') || // Service unavailable
        error.name === 'AbortError' || // Timeout
        error.message.includes('fetch');
      
      if (isRetryableError && retries > 1) {
        console.warn(`Retrying Gemini API call, ${retries - 1} attempts remaining. Error: ${error.message}`);
        retries--;
        
        // Exponential backoff with jitter
        const jitter = Math.random() * 0.3 * backoffDelay;
        await new Promise(resolve => setTimeout(resolve, backoffDelay + jitter));
        backoffDelay *= 2; // Double the backoff delay for next retry
      } else {
        // Don't retry other types of errors or if we're on our last retry
        throw error;
      }
    }
  }
  
  // If we've exhausted retries
  throw lastError || new Error("Failed to get response from Gemini after multiple attempts. The service may be experiencing issues. Please try again later.");
}

// Function to check if the Gemini integration is working (Optional, requires a simple endpoint in proxy)
export async function checkGeminiConnection(): Promise<boolean> {
  try {
    const apiKey = localStorage.getItem('gemini_api_key');
    // If a more specific check is needed, the proxy function would need to support it.
    // For now, we assume if the proxy can be invoked, it's a basic check.
    // A true check would involve making a lightweight API call to Gemini using the key.
    const { error } = await supabase.functions.invoke('gemini-proxy', {
      body: {
        // A lightweight request, e.g., list models if Gemini API supports it without extra cost
        // Or a very simple prompt to a free/cheap model endpoint
        action: 'check_connection', // Add an action to tell the proxy to perform a check
        apiKey: apiKey 
      }
    });
    if (error) {
        console.error('Gemini connection check failed via proxy:', error);
        return false;
    }
    return true;
  } catch (error) {
    console.error('Error checking Gemini connection:', error);
    return false;
  }
}